import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { useLogtoAdapter } from '@lib/logto';
import { currentUserKey } from '@lib/cache/users';
import { getCurrentUserProfile } from '@api-client/api-client';
import type { UserProfileResponse } from '@models';
import { useLocation } from 'react-router-dom';
import { AuthSessionState } from '@providers/AuthProvider';
import { useAuthStore } from '@stores/authStore';
import { logger } from '@utils/logger';

/**
 * Hook to manage the current user profile with React Query
 * Handles authentication state and user data fetching with automatic deduplication
 *
 * @param sessionState - The current auth session state from AuthProvider
 *                       Used to prevent queries when session_expired
 */
export const useCurrentUser = (sessionState?: AuthSessionState) => {
  const { isAuthenticated, isLoading: logtoLoading } = useLogtoAdapter();
  const queryClient = useQueryClient();
  const location = useLocation();

  // AIDEV-NOTE: Prevent fetching user during callback flow to avoid race condition
  // Logto SDK's isAuthenticated becomes true BEFORE tokens are stored
  // This causes premature API calls that fail with 401 errors
  const isOnCallbackRoute = location.pathname === '/callback';

  // AIDEV-NOTE: Block queries when session is expired or reauthenticating to prevent
  // the infinite loop of 401 -> refresh -> invalid_grant -> retry pattern
  const isSessionBlocked =
    sessionState === AuthSessionState.SESSION_EXPIRED ||
    sessionState === AuthSessionState.REAUTHENTICATING;

  // AIDEV-NOTE: a sign-out in progress blocks EVERY observer of this key, not
  // just AuthProvider's. `clearAuthState` removes the query, and on native the
  // Logto adapter stays authenticated for the whole system-browser round trip,
  // so the next render of any mounted observer rebuilt it and refetched
  // /users/me with the leaving session's bearer. That brought the signed-out
  // user back. `sessionState` cannot carry this: it is a parameter only
  // AuthProvider passes, and ReferralCodeSync, BadgeProvider and
  // EventStreamProvider all observe this key with no arguments.
  //
  // Subscribed, not read through `getState()`: `enabled` has to change for the
  // observer to disable, and reading mutable external state during render
  // without subscribing tears.
  const isSigningOut = useAuthStore(state => state.isSigningOut);

  // Query for fetching user profile
  const {
    data: user,
    isLoading: userLoading,
    error,
    refetch: refetchUser,
  } = useQuery<UserProfileResponse | null>({
    queryKey: currentUserKey(),
    queryFn: async () => {
      // Only fetch if authenticated and session is valid
      //
      // The sign-out term is read LIVE here rather than from the render-time
      // binding above, because this guard's whole job is to catch a fetch that
      // `enabled` cannot: an explicit `refetch()` runs the queryFn whatever
      // `enabled` says, and a sign-out can begin after the render that built
      // these options. This read is what actually stops the request; the
      // `enabled` term below keeps the query from being scheduled in the first
      // place. signOutLatch.integration.test.tsx pins each one with its own row.
      if (!isAuthenticated || isSessionBlocked || useAuthStore.getState().isSigningOut) {
        return null;
      }

      try {
        // JWT ID token authentication handled automatically by API client interceptor
        const userProfile = await getCurrentUserProfile();
        return userProfile;
      } catch (err: unknown) {
        logger.debug(`error from /me`, err);
        let status: number | undefined;
        if (isAxiosError(err)) {
          status = err.response?.status;
        } else if (typeof err === 'object' && err !== null && 'status' in err) {
          const maybeStatus = (err as { status?: unknown }).status;
          if (typeof maybeStatus === 'number') {
            status = maybeStatus;
          }
        }

        // AIDEV-NOTE: Removed navigate('/signup') from here - hooks should NOT navigate directly.
        // Navigation is handled by AuthProvider which checks for 404 errors and navigates appropriately.
        // This prevents race conditions where navigation happens during Logto redirect flow.

        if (status === 401) {
          // AIDEV-NOTE: Don't log warning during session expiration - it's expected
          // The AuthProvider's handleInvalidGrant will handle this
          logger.warn(
            'Received 401 while fetching user profile; token refresh should already have been attempted by the API client.',
          );
        }

        logger.error('Failed to fetch user profile:', err);

        let enrichedError: Error & { status?: number; originalError?: unknown };
        if (err instanceof Error) {
          enrichedError = err;
        } else {
          let errMessage: string | undefined;
          if (typeof err === 'object' && err !== null && 'message' in err) {
            const maybeMessage = (err as { message?: unknown }).message;
            if (typeof maybeMessage === 'string') {
              errMessage = maybeMessage;
            }
          }
          const fallbackMessage = errMessage ?? 'Failed to fetch user profile';
          enrichedError = new Error(fallbackMessage);
          enrichedError.originalError = err;
        }

        if (typeof status === 'number') {
          enrichedError.status = status;
        }

        throw enrichedError;
      }
    },
    // Enable the query when authenticated, not loading, not on callback route, AND session is valid
    // AIDEV-NOTE: isSessionBlocked prevents queries during re-authentication flow
    enabled:
      isAuthenticated && !logtoLoading && !isOnCallbackRoute && !isSessionBlocked && !isSigningOut,
    // Prevent unnecessary refetches
    staleTime: 30 * 1000, // Consider data fresh for 30 seconds
    gcTime: 5 * 60 * 1000, // Keep in cache for 5 minutes
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    // Retry configuration
    retry: (failureCount: number, error: unknown) => {
      // Don't retry on 404 (user not found) - they need to sign up
      let status: number | undefined;
      if (isAxiosError(error)) {
        status = error.response?.status;
      } else if (typeof error === 'object' && error !== null && 'status' in error) {
        const maybeStatus = (error as { status?: unknown }).status;
        if (typeof maybeStatus === 'number') {
          status = maybeStatus;
        }
      }
      if (status === 404) {
        return false;
      }
      // Don't retry on 401 (unauthorized) - token refresh already attempted by API client
      // AIDEV-NOTE: Re-auth flow will redirect to login, no point retrying
      if (status === 401) {
        return false;
      }
      // Retry up to 3 times for other errors
      return failureCount < 3;
    },
    retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
  });

  // Combined loading state
  const isLoading = logtoLoading || userLoading;

  // Clear user data on logout. Memoized so consumers (AuthProvider's
  // tolerance-window effect, useSdkLoadingWatchdog, useSignInOut, etc.) can
  // include this in dep arrays without triggering re-evaluation on every
  // render. Identity is stable per `queryClient` reference, which is itself
  // stable for the QueryClientProvider's lifetime.
  const clearUser = useCallback(() => {
    queryClient.setQueryData(currentUserKey(), null);
    queryClient.removeQueries({ queryKey: currentUserKey() });
  }, [queryClient]);

  return {
    user,
    isLoading,
    isAuthenticated: isAuthenticated && !!user,
    error,
    refetchUser,
    clearUser,
  };
};
